import React, { useEffect, useState, useRef, useCallback } from 'react';

interface LogEntry {
  id: string;
  timestamp: Date;
  level: 'log' | 'error' | 'warn' | 'info' | 'debug';
  message: string;
  data?: unknown[];
}

interface ConsoleOverlayProps {
  isVisible?: boolean;
  maxLogs?: number;
  showTimestamp?: boolean;
  showLevel?: boolean;
}

interface Position {
  x: number;
  y: number;
}

const ConsoleOverlay: React.FC<ConsoleOverlayProps> = ({
  isVisible = true,
  maxLogs = 100,
  showTimestamp = true,
  showLevel = true,
}) => {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [isExpanded, setIsExpanded] = useState(false);
  const [position, setPosition] = useState<Position>({ x: 10, y: 10 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState<Position>({ x: 0, y: 0 });
  const logsEndRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    logsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [logs]);

  // Drag functionality
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.target !== e.currentTarget) return; // Only drag from header

    setIsDragging(true);
    const rect = overlayRef.current?.getBoundingClientRect();
    if (rect) {
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
    }
  }, []);

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging) return;

      const newX = e.clientX - dragOffset.x;
      const newY = e.clientY - dragOffset.y;

      // Keep within viewport bounds
      const maxX = window.innerWidth - (isExpanded ? 600 : 300);
      const maxY = window.innerHeight - (isExpanded ? 80 : 200);

      setPosition({
        x: Math.max(0, Math.min(newX, maxX)),
        y: Math.max(0, Math.min(newY, maxY)),
      });
    },
    [isDragging, dragOffset, isExpanded]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  useEffect(() => {
    if (!isVisible) return;

    // Store original console methods
    const originalConsole = {
      log: console.log,
      error: console.error,
      warn: console.warn,
      info: console.info,
      debug: console.debug,
    };

    // Store original fetch
    const originalFetch = window.fetch;

    // Create log entry
    const createLogEntry = (level: LogEntry['level'], ...args: unknown[]): LogEntry => ({
      id: `${Date.now()}-${Math.random()}`,
      timestamp: new Date(),
      level,
      message: args.map((arg) => (typeof arg === 'object' ? JSON.stringify(arg, null, 2) : String(arg))).join(' '),
      data: args.length > 1 ? args : undefined,
    });

    // Override console methods
    console.log = (...args) => {
      originalConsole.log(...args);
      setLogs((prev) => {
        const newLogs = [...prev, createLogEntry('log', ...args)];
        return newLogs.slice(-maxLogs);
      });
    };

    console.error = (...args) => {
      originalConsole.error(...args);
      setLogs((prev) => {
        const newLogs = [...prev, createLogEntry('error', ...args)];
        return newLogs.slice(-maxLogs);
      });
    };

    console.warn = (...args) => {
      originalConsole.warn(...args);
      setLogs((prev) => {
        const newLogs = [...prev, createLogEntry('warn', ...args)];
        return newLogs.slice(-maxLogs);
      });
    };

    console.info = (...args) => {
      originalConsole.info(...args);
      setLogs((prev) => {
        const newLogs = [...prev, createLogEntry('info', ...args)];
        return newLogs.slice(-maxLogs);
      });
    };

    console.debug = (...args) => {
      originalConsole.debug(...args);
      setLogs((prev) => {
        const newLogs = [...prev, createLogEntry('debug', ...args)];
        return newLogs.slice(-maxLogs);
      });
    };

    // Override fetch to capture network errors
    window.fetch = async (...args) => {
      try {
        const response = await originalFetch(...args);

        // Log successful requests in debug mode
        if (response.status >= 400) {
          const errorText = await response.text().catch(() => 'Unable to read response');
          setLogs((prev) => {
            const newLogs = [
              ...prev,
              createLogEntry('error', `Network Error: ${response.status} ${response.statusText}`, {
                url: args[0],
                status: response.status,
                statusText: response.statusText,
                response: errorText,
              }),
            ];
            return newLogs.slice(-maxLogs);
          });
        }

        return response;
      } catch (error) {
        // Log network errors
        setLogs((prev) => {
          const newLogs = [
            ...prev,
            createLogEntry('error', `Network Error: ${error}`, {
              url: args[0],
              error: error,
              type: 'fetch_error',
            }),
          ];
          return newLogs.slice(-maxLogs);
        });
        throw error;
      }
    };

    // Restore original methods on cleanup
    return () => {
      console.log = originalConsole.log;
      console.error = originalConsole.error;
      console.warn = originalConsole.warn;
      console.info = originalConsole.info;
      console.debug = originalConsole.debug;
      window.fetch = originalFetch;
    };
  }, [isVisible, maxLogs]);

  const getLevelColor = (level: LogEntry['level']) => {
    switch (level) {
      case 'error':
        return '#ff4444';
      case 'warn':
        return '#ffaa00';
      case 'info':
        return '#00aaff';
      case 'debug':
        return '#888888';
      default:
        return '#ffffff';
    }
  };

  const getLevelIcon = (level: LogEntry['level']) => {
    switch (level) {
      case 'error':
        return '❌';
      case 'warn':
        return '⚠️';
      case 'info':
        return 'ℹ️';
      case 'debug':
        return '🐛';
      default:
        return '📝';
    }
  };

  const formatTimestamp = (date: Date) => {
    return date.toLocaleTimeString();
  };

  const clearLogs = () => {
    setLogs([]);
  };

  if (!isVisible) return null;

  return (
    <div
      ref={overlayRef}
      style={{
        position: 'fixed',
        top: `${position.y}px`,
        left: `${position.x}px`,
        width: isExpanded ? '600px' : '300px',
        maxHeight: isExpanded ? '80vh' : '200px',
        backgroundColor: 'rgba(0, 0, 0, 0.9)',
        color: '#fff',
        fontFamily: 'monospace',
        fontSize: '12px',
        borderRadius: '8px',
        zIndex: 10000,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        border: '1px solid #333',
        userSelect: 'none',
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '8px 12px',
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
          borderBottom: '1px solid #333',
          cursor: 'move',
        }}
        onMouseDown={handleMouseDown}
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            setIsExpanded(!isExpanded);
          }
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span>🐛 Console Logs</span>
          <span style={{ fontSize: '10px', opacity: 0.7 }}>({logs.length} entries)</span>
        </div>
        <div style={{ display: 'flex', gap: '4px' }}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              clearLogs();
            }}
            style={{
              background: 'rgba(255, 68, 68, 0.8)',
              border: 'none',
              color: 'white',
              padding: '2px 6px',
              borderRadius: '4px',
              fontSize: '10px',
              cursor: 'pointer',
            }}
          >
            Clear
          </button>
          <span>{isExpanded ? '📉' : '📈'}</span>
        </div>
      </div>

      {/* Logs Container */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '8px',
          maxHeight: isExpanded ? 'calc(80vh - 40px)' : '160px',
        }}
      >
        {logs.length === 0 ? (
          <div style={{ textAlign: 'center', opacity: 0.7, padding: '20px' }}>No logs yet...</div>
        ) : (
          logs.map((log) => (
            <div
              key={log.id}
              style={{
                marginBottom: '4px',
                padding: '4px 8px',
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '4px',
                borderLeft: `3px solid ${getLevelColor(log.level)}`,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                {showLevel && (
                  <span style={{ color: getLevelColor(log.level), fontSize: '10px' }}>{getLevelIcon(log.level)}</span>
                )}
                <div style={{ flex: 1, wordBreak: 'break-word' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2px' }}>
                    {showTimestamp && (
                      <span style={{ opacity: 0.7, fontSize: '10px' }}>{formatTimestamp(log.timestamp)}</span>
                    )}
                    {showLevel && (
                      <span style={{ color: getLevelColor(log.level), fontSize: '10px' }}>
                        {log.level.toUpperCase()}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: '11px', lineHeight: '1.4' }}>{log.message}</div>
                  {log.data && log.data.length > 1 && (
                    <details style={{ marginTop: '4px' }}>
                      <summary style={{ cursor: 'pointer', fontSize: '10px', opacity: 0.7 }}>
                        Show data ({log.data.length} items)
                      </summary>
                      <pre
                        style={{
                          fontSize: '10px',
                          opacity: 0.8,
                          marginTop: '4px',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                        }}
                      >
                        {JSON.stringify(log.data, null, 2)}
                      </pre>
                    </details>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
        <div ref={logsEndRef} />
      </div>
    </div>
  );
};

export default ConsoleOverlay;
